import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './ScreenManager.css'

const DEFAULT_SCREEN_CONFIG = [
  { id: 'data_entry',   enabled: true },
  { id: 'safety_rules', enabled: true },
  { id: 'quiz',         enabled: true },
]

const SCREEN_META = {
  header:       { label: 'Willkommen',        description: 'Begrüßungsseite mit Datenschutzhinweis', required: true },
  data_entry:   { label: 'Daten eingeben',    description: 'Besucherdaten: Name, E-Mail, Telefon, Datum', required: true },
  safety_rules: { label: 'Sicherheitsregeln', description: 'Sicherheitsregeln mit Bestätigungscheckbox' },
  quiz:         { label: 'Quiz',              description: 'Wissenstest — Besucher muss alle Fragen korrekt beantworten' },
}

const ScreenManager = () => {
  const { apiFetch } = useAuth()

  const [tenants, setTenants]               = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [screens, setScreens]               = useState(DEFAULT_SCREEN_CONFIG)
  const [loading, setLoading]               = useState(true)
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState(null)
  const [success, setSuccess]               = useState(false)

  // Drag state
  const dragIdx = useRef(null)

  // Load tenant list
  useEffect(() => {
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
        else setLoading(false)
      })
      .catch(() => {
        setError('Mandanten konnten nicht geladen werden')
        setLoading(false)
      })
  }, [])

  // Load screen config when tenant changes
  useEffect(() => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    apiFetch(`/api/admin/screen-config/${selectedTenant}`)
      .then(r => r.json())
      .then(data => {
        const config = Array.isArray(data.screen_config) ? data.screen_config : DEFAULT_SCREEN_CONFIG
        // Exclude fixed screens (header) — they're always shown as locked rows
        const configurable = config.filter(s => s.id !== 'header')
        // Ensure all known configurable screens are present
        const merged = DEFAULT_SCREEN_CONFIG.map(def => {
          const saved = configurable.find(s => s.id === def.id)
          return saved || def
        })
        // Respect saved order
        const orderedIds = configurable.map(s => s.id)
        merged.sort((a, b) => {
          const ai = orderedIds.indexOf(a.id)
          const bi = orderedIds.indexOf(b.id)
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
        })
        setScreens(merged)
      })
      .catch(() => setError('Bildschirm-Konfiguration konnte nicht geladen werden'))
      .finally(() => setLoading(false))
  }, [selectedTenant])

  const toggleScreen = (id) => {
    if (SCREEN_META[id]?.required) return
    setScreens(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s))
  }

  // ── HTML5 drag and drop ──────────────────────────────────────────────────
  const handleDragStart = (e, idx) => {
    dragIdx.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragIdx.current === null || dragIdx.current === idx) return
    setScreens(prev => {
      const next = [...prev]
      const [dragged] = next.splice(dragIdx.current, 1)
      next.splice(idx, 0, dragged)
      dragIdx.current = idx
      return next
    })
  }

  const handleDrop = () => {
    dragIdx.current = null
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      // Always prepend the fixed screens in order before saving
      const fullConfig = [{ id: 'header', enabled: true }, ...screens]
      const res  = await apiFetch(`/api/admin/screen-config/${selectedTenant}`, {
        method: 'PUT',
        body:   JSON.stringify(fullConfig),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Bildschirme</h1>
          <p>Reihenfolge und Sichtbarkeit der Check-In-Bildschirme pro Mandant</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Gespeichert</div>}

      {tenants.length === 0 && !loading ? (
        <div className="card">
          <p className="sm-empty">Noch keine Mandanten vorhanden.</p>
        </div>
      ) : (
        <>
          <div className="card sm-tenant-card">
            <div className="form-group sm-tenant-form-group">
              <label>Mandant</label>
              <select
                className="form-control"
                value={selectedTenant}
                onChange={e => setSelectedTenant(e.target.value)}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card">
            <div className="section-title">Bildschirm-Reihenfolge</div>
            <p className="sm-hint">
              Ziehen zum Umsortieren. Sprache, Willkommen und QR-Code sind immer fixiert.
            </p>

            {loading ? (
              <div className="loading-state"><span className="spinner" /></div>
            ) : (
              <>
                {/* Fixed first: Language picker */}
                <div className="sm-screen-row sm-screen-row--locked">
                  <span className="sm-drag-handle sm-drag-handle--disabled">≡</span>
                  <div className="sm-screen-info">
                    <div className="sm-screen-label">Sprache wählen</div>
                    <div className="sm-screen-desc">Sprachauswahl — immer erster Bildschirm</div>
                  </div>
                  <span className="sm-lock-badge">Fixiert</span>
                </div>

                {/* Fixed second: Welcome */}
                <div className="sm-screen-row sm-screen-row--locked">
                  <span className="sm-drag-handle sm-drag-handle--disabled">≡</span>
                  <div className="sm-screen-info">
                    <div className="sm-screen-label">Willkommen</div>
                    <div className="sm-screen-desc">Begrüßungsseite mit Datenschutzhinweis — immer zweiter Bildschirm</div>
                  </div>
                  <span className="sm-lock-badge">Fixiert</span>
                </div>

                {/* Configurable screens */}
                {screens.map((screen, idx) => {
                  const meta     = SCREEN_META[screen.id] || {}
                  const required = !!meta.required
                  return (
                    <div
                      key={screen.id}
                      draggable
                      onDragStart={e => handleDragStart(e, idx)}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDrop={handleDrop}
                      className="sm-screen-row sm-screen-row--draggable"
                    >
                      <span className="sm-drag-handle" title="Ziehen zum Verschieben">≡</span>
                      <div className="sm-screen-info">
                        <div className="sm-screen-label">{meta.label || screen.id}</div>
                        <div className="sm-screen-desc">{meta.description || ''}</div>
                      </div>
                      {required ? (
                        <span className="sm-lock-badge">Pflicht</span>
                      ) : (
                        <label className="toggle sm-toggle">
                          <input
                            type="checkbox"
                            checked={screen.enabled}
                            onChange={() => toggleScreen(screen.id)}
                          />
                          <span className="toggle-slider" />
                        </label>
                      )}
                    </div>
                  )
                })}

                {/* Fixed last: QR Code */}
                <div className="sm-screen-row sm-screen-row--locked">
                  <span className="sm-drag-handle sm-drag-handle--disabled">≡</span>
                  <div className="sm-screen-info">
                    <div className="sm-screen-label">QR-Code</div>
                    <div className="sm-screen-desc">Abschluss-Bildschirm mit QR-Code — immer letzter Bildschirm</div>
                  </div>
                  <span className="sm-lock-badge">Fixiert</span>
                </div>

                <div className="sm-save-row">
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><span className="spinner spinner-sm" /> Speichern...</>
                      : 'Speichern'}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ScreenManager
